import React, { useEffect, useState } from "react";
import { FaChartLine } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type Batch = { id: number; name: string };

type BatchCourseProgressDto = {
  batchId: number;
  c1: boolean;
  c2: boolean;
  c3: boolean;
  c4: boolean;
  c5: boolean;
  c6: boolean;
  c7: boolean;
  c8: boolean;
  c9: boolean;
  c10: boolean;
  c11: boolean;
  c12: boolean;
  c13: boolean;
  c14: boolean;
  examDone: boolean;
  updatedAt?: string | null;
};

const defaultProgress: BatchCourseProgressDto = {
  batchId: 0,
  c1: false,
  c2: false,
  c3: false,
  c4: false,
  c5: false,
  c6: false,
  c7: false,
  c8: false,
  c9: false,
  c10: false,
  c11: false,
  c12: false,
  c13: false,
  c14: false,
  examDone: false,
  updatedAt: null,
};

const LABELS = [
  "Chapter 1",
  "Chapter 2",
  "Chapter 3",
  "Chapter 4",
  "Chapter 5",
  "Chapter 6",
  "Chapter 7",
  "Chapter 8",
  "Chapter 9",
  "Chapter 10",
  "Chapter 11",
  "Chapter 12",
  "Chapter 13",
  "Chapter 14",
  "Final Exam",
];

const MentorBatchCourseProgress: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [progress, setProgress] =
    useState<BatchCourseProgressDto>(defaultProgress);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string>("");

  useEffect(() => {
    setLoadingBatches(true);
    fetch(`${API_BASE}/api/mentor/my-batches`, { credentials: "include" })
      .then((r) => r.json())
      .then((data: any[]) => {
        const list = (Array.isArray(data) ? data : []).map((b) => ({
          id: b.id,
          name: b.name,
        }));
        setBatches(list);
        if (list.length > 0) setSelectedBatchId(list[0].id);
      })
      .catch(() => setMsg("Failed to load batches"))
      .finally(() => setLoadingBatches(false));
  }, []);

  useEffect(() => {
    if (!selectedBatchId) {
      setProgress(defaultProgress);
      return;
    }
    setLoadingProgress(true);
    setMsg("");
    fetch(
      `${API_BASE}/api/mentor/batch/${selectedBatchId}/batch-course-progress`,
      {
        credentials: "include",
      }
    )
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((p: BatchCourseProgressDto) => setProgress({ ...p }))
      .catch(() =>
        setProgress({ ...defaultProgress, batchId: selectedBatchId })
      )
      .finally(() => setLoadingProgress(false));
  }, [selectedBatchId]);

  const toggle = (index: number) => {
    const key =
      index === 14
        ? "examDone"
        : (`c${index + 1}` as keyof BatchCourseProgressDto);
    setProgress((prev) => ({ ...prev, [key]: !prev[key] as any }));
  };

  const setAll = (value: boolean) => {
    setProgress((prev) => ({
      ...prev,
      c1: value,
      c2: value,
      c3: value,
      c4: value,
      c5: value,
      c6: value,
      c7: value,
      c8: value,
      c9: value,
      c10: value,
      c11: value,
      c12: value,
      c13: value,
      c14: value,
      examDone: value,
    }));
  };

  const save = async () => {
    if (!selectedBatchId) return;
    setSaving(true);
    setMsg("");
    const payload = {
      c1: progress.c1,
      c2: progress.c2,
      c3: progress.c3,
      c4: progress.c4,
      c5: progress.c5,
      c6: progress.c6,
      c7: progress.c7,
      c8: progress.c8,
      c9: progress.c9,
      c10: progress.c10,
      c11: progress.c11,
      c12: progress.c12,
      c13: progress.c13,
      c14: progress.c14,
      examDone: progress.examDone,
    };
    try {
      const res = await fetch(
        `${API_BASE}/api/mentor/batch/${selectedBatchId}/batch-course-progress`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Save failed");

      const json = await res.json();
      const dto: BatchCourseProgressDto = json?.data ?? json; // <-- unwrap
      setProgress(dto); // <-- set the real dto
      setMsg("Saved!");
    } catch (e: any) {
      setMsg(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaChartLine />}
        title="Batch Course Progress"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Batch-wise Course Progress" },
        ]}
      />

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <label className="font-semibold mr-3">Select Batch:</label>
              <select
                className="border px-4 py-2 rounded"
                disabled={loadingBatches}
                value={selectedBatchId ?? ""}
                onChange={(e) => setSelectedBatchId(Number(e.target.value))}
              >
                {loadingBatches && <option>Loading...</option>}
                {!loadingBatches && batches.length === 0 && (
                  <option>No batches</option>
                )}
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded"
                onClick={() => setAll(true)}
              >
                Check All
              </button>
              <button
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded"
                onClick={() => setAll(false)}
              >
                Uncheck All
              </button>
            </div>
          </div>

          {msg && <div className="mt-3 text-sm">{msg}</div>}

          {loadingProgress ? (
            <div className="mt-6 text-[#009fe3]">Loading progress…</div>
          ) : selectedBatchId ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
                {LABELS.map((label, idx) => {
                  const checked =
                    idx === 14
                      ? progress.examDone
                      : ((progress as any)[`c${idx + 1}`] as boolean);
                  return (
                    <label
                      key={idx}
                      className="flex items-center gap-3 border rounded-lg p-3 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={!!checked}
                        onChange={() => toggle(idx)}
                      />
                      <span className="font-medium">{label}</span>
                    </label>
                  );
                })}
              </div>

              <div className="mt-6">
                <button
                  onClick={save}
                  disabled={saving}
                  className="bg-[#009fe3] hover:opacity-90 text-white px-5 py-2 rounded-lg shadow"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                {progress.updatedAt && (
                  <span className="ml-3 text-xs text-gray-500">
                    Last updated:{" "}
                    {new Date(progress.updatedAt).toLocaleString()}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="mt-6 text-gray-500">
              Select a batch to continue.
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MentorBatchCourseProgress;
